import React, { createContext, useContext, useState, ReactNode, useEffect } from "react";

import { useGetPolicies } from "@/api/useGetPolicies";
import { Permission } from "@/utils/policies";

interface PermissionsContextType {
  hasPermission: (_permission: Permission) => boolean;
  hasAnyPermission: (_permissions: Permission[]) => boolean;
  hasAllPermissions: (_permissions: Permission[]) => boolean;
  setPermissions: (_permissions: Permission[]) => void;
}

const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined);

export const PermissionsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { data: policiesResponse } = useGetPolicies();
  const [userPermissions, setUserPermissions] = useState<Set<string>>(new Set());

  const loadPermissions = (permissionsFromDB: string[]) => {
    setUserPermissions(new Set(permissionsFromDB));
  };

  const hasPermission = (permission: Permission): boolean => {
    return userPermissions.has(permission);
  };

  const hasAnyPermission = (permissions: Permission[]): boolean => {
    return permissions.some((permission) => userPermissions.has(permission));
  };

  const hasAllPermissions = (permissions: Permission[]): boolean => {
    return permissions.every((permission) => userPermissions.has(permission));
  };
  useEffect(() => {
    if (policiesResponse?.status === 200) {
      const { policies } = policiesResponse;
      const policiesMapped = policies.flatMap((p) => p.resource_action).flatMap((ra) => ra.action);
      loadPermissions(Array.from(new Set(policiesMapped)));
    }
  }, [policiesResponse]);

  const contextValue = React.useMemo(
    () => ({
      hasPermission,
      hasAnyPermission,
      hasAllPermissions,
      setPermissions: loadPermissions,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userPermissions]
  );

  return <PermissionsContext.Provider value={contextValue}>{children}</PermissionsContext.Provider>;
};

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error("usePermissions must be used inside PermissionsProvider");
  }
  return context;
};
